import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * S16-2 — Menu déroulant générique « menu button » (pattern APG), extrait de
 * l'ancien `AccountMenu` (S13-4) pour être partagé entre le menu du compte
 * connecté et le menu du visiteur anonyme (« tout dans l'avatar », décision PO
 * 2026-06-12).
 *
 * Le composant porte toute l'**accessibilité clavier** (impératif S13-4) :
 * - Trigger : `aria-haspopup="menu"`, `aria-expanded`, `aria-controls`.
 * - Conteneur : `role="menu"` + `aria-labelledby` (le trigger) ; items
 *   `role="menuitem"`.
 * - Clavier : `ArrowDown`/`ArrowUp` (circulaire), `Home`/`End`, `Enter`/`Space`
 *   pour ouvrir et activer ; `Escape` ferme et **rend le focus au trigger** ;
 *   `Tab` ferme (focus naturel) ; à l'ouverture, le focus va sur le 1er item
 *   (ou le dernier si ouverture via `ArrowUp`).
 * - Clic extérieur ferme (sans rendre le focus, comportement souris attendu).
 *
 * Pas de dépendance lourde (pas de Radix Menu) — implémentation maison alignée
 * sur le pattern APG « menu button » (garde-fou ADR-001 : pas de nouvelle dép
 * sans validation Architect).
 */
export interface DropdownMenuItemSpec {
  /** Libellé visible et nom accessible de l'item. */
  label: string;
  icon: JSX.Element;
  onSelect: () => void;
  /**
   * `true` si l'item correspond à la **page courante** : il est marqué visuellement
   * (fond gris persistant + `aria-current="page"`) et reçoit le focus à
   * l'ouverture (au lieu du 1er item par défaut). Au plus un item devrait l'avoir.
   */
  current?: boolean;
}

export interface DropdownMenuProps {
  /** Identifiant unique (préfixe des `id` trigger/menu pour `aria-*`). */
  id: string;
  /** Contenu du bouton déclencheur (icône avatar + libellé éventuel). */
  triggerContent: JSX.Element;
  /** Nom accessible du trigger (`aria-label`) — requis si pas de texte. */
  triggerAriaLabel?: string;
  /** Classe Tailwind du bouton déclencheur. */
  triggerClassName?: string;
  /** Entrées du menu, dans l'ordre d'affichage. */
  items: DropdownMenuItemSpec[];
}

export function DropdownMenu({
  id,
  triggerContent,
  triggerAriaLabel,
  triggerClassName,
  items,
}: DropdownMenuProps): JSX.Element {
  const triggerId = `${id}-trigger`;
  const menuId = `${id}-menu`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Index d'ouverture par défaut : l'item « page courante » s'il existe, sinon
  // le 1er (pattern APG). Évite que le 1er item paraisse toujours « sélectionné »
  // (focus gris) alors qu'on est sur une autre page.
  const currentIndex = items.findIndex((item) => item.current);
  const defaultOpenIndex = currentIndex >= 0 ? currentIndex : 0;

  const closeMenu = useCallback((returnFocus: boolean): void => {
    setOpen(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  const openMenu = useCallback((index: number): void => {
    setActiveIndex(index);
    setOpen(true);
  }, []);

  // Focus l'item actif quand le menu est ouvert / l'index change.
  useEffect(() => {
    if (!open) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  // Clic extérieur → ferme (sans rendre le focus, comportement souris).
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const onTriggerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    switch (event.key) {
      case "ArrowDown":
      case "Enter":
      case " ":
        event.preventDefault();
        openMenu(defaultOpenIndex);
        break;
      case "ArrowUp":
        event.preventDefault();
        openMenu(items.length - 1);
        break;
      default:
        break;
    }
  };

  const onItemKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index + 1) % items.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index - 1 + items.length) % items.length);
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(items.length - 1);
        break;
      case "Escape":
        event.preventDefault();
        closeMenu(true);
        break;
      case "Tab":
        // Laisse le focus sortir naturellement, mais ferme le menu.
        setOpen(false);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        closeMenu(true);
        items[index]?.onSelect();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-label={triggerAriaLabel}
        className={cn(
          // S16-1 — `min-h-[44px]` : cible tactile confortable au pouce sur
          // mobile (recommandation WCAG 2.5.5 ≥ 44 px).
          "flex min-h-[44px] items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900",
          triggerClassName,
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? closeMenu(false) : openMenu(defaultOpenIndex))}
        onKeyDown={onTriggerKeyDown}
      >
        {triggerContent}
        <ChevronDown
          className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          id={menuId}
          role="menu"
          aria-labelledby={triggerId}
          className="absolute right-0 z-[1100] mt-1 w-52 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {items.map((item, index) => (
            <li key={item.label} role="none">
              <button
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                role="menuitem"
                aria-current={item.current ? "page" : undefined}
                tabIndex={activeIndex === index ? 0 : -1}
                className={cn(
                  "flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none",
                  // Page courante : fond gris persistant (repère « vous êtes ici »).
                  item.current && "bg-slate-100 font-medium text-slate-900",
                )}
                onClick={() => {
                  closeMenu(true);
                  item.onSelect();
                }}
                onKeyDown={(event) => onItemKeyDown(event, index)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
